import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ArrowLeft, CalendarDays, Printer } from "lucide-react";
import { displayName } from "@/lib/athleteName";
import { ScoreDialog } from "@/components/competitive/ScoreDialog";
import { ParticipantsTab } from "@/components/competitive/ParticipantsTab";
import { PoolCard } from "@/components/competitive/PoolCard";
import { BracketTab } from "@/components/competitive/BracketTab";
import { RankingTab } from "@/components/competitive/RankingTab";
import { PoolsEditor } from "@/components/competitive/PoolsEditor";

const STATUS = { draft: "Bozza", pools: "Gironi in corso", direct_elimination: "Eliminazione diretta", completed: "Conclusa" };

export default function CompetitiveSession() {
  const { id } = useParams();
  const [detail, setDetail] = useState(null);
  const [athletes, setAthletes] = useState([]);
  const [groups, setGroups] = useState([]);
  const [score, setScore] = useState(null); // { bout, nameA, nameB, hits }
  const [tab, setTab] = useState(new URLSearchParams(window.location.search).get("tab") || "participants");
  const [ranking, setRanking] = useState({});
  const [editPools, setEditPools] = useState(false);

  const load = useCallback(() => api.get(`/competitive-sessions/${id}`).then((r) => setDetail(r.data)).catch(() => toast.error("Simulazione non trovata")), [id]);
  useEffect(() => {
    load();
    Promise.all([api.get("/athletes"), api.get("/groups")]).then(([a, g]) => { setAthletes(a.data || []); setGroups(g.data || []); });
    api.get("/competitive-ranking").then((r) => setRanking(Object.fromEntries((r.data.ranking || []).map((x) => [x.athlete_id, x])))).catch(() => {});
  }, [load]);

  const athleteById = useMemo(() => Object.fromEntries(athletes.map((a) => [a.id, a])), [athletes]);
  const nameOf = useCallback((key) => {
    if (!key) return "—";
    const p = (detail?.session.participants || []).find((x) => x.key === key);
    if (p?.athlete_id) { const a = athleteById[p.athlete_id]; return a ? displayName(a) : "Atleta CSP"; }
    if (p?.external_name) return p.external_name;
    const a = athleteById[key]; return a ? displayName(a) : key;
  }, [detail, athleteById]);

  const saveParticipants = async (participants) => {
    try { const { data } = await api.put(`/competitive-sessions/${id}`, { ...detail.session, participants }); setDetail(data); }
    catch (e) { toast.error(e.response?.data?.detail || "Errore salvataggio"); }
  };
  const distribute = async (n) => {
    // distribuzione a serpentina per ranking interno (9999 = senza storico, in coda nell'ordine di inserimento)
    const keys = [...detail.session.participants].sort((x, y) => rankOf(x.key) - rankOf(y.key)).map((p) => p.key);
    const pools = Array.from({ length: n }, () => []);
    keys.forEach((k, i) => { const round = Math.floor(i / n); const idx = round % 2 === 0 ? i % n : n - 1 - (i % n); pools[idx].push(k); });
    const valid = pools.filter((p) => p.length >= 2);
    if (valid.length < pools.length) { toast.error("Troppi gironi per i partecipanti: servono almeno 2 atleti per girone"); return; }
    await savePools(valid, `${valid.length} gironi generati per ranking interno`);
  };
  const savePools = async (pools, msg) => {
    try { const { data } = await api.post(`/competitive-sessions/${id}/pools`, { pools }); setDetail(data); setTab("pools"); setEditPools(false); toast.success(msg || "Gironi salvati"); }
    catch (e) { toast.error(e.response?.data?.detail || "Errore gironi"); }
  };
  const rankOf = useCallback((key) => {
    const p = (detail?.session.participants || []).find((x) => x.key === key);
    return (p?.athlete_id && ranking[p.athlete_id]?.rank) || 9999;
  }, [detail, ranking]);
  const openBout = (bout, match) => {
    if (!bout) return;
    setScore({ bout, nameA: nameOf(bout.participant_a), nameB: nameOf(bout.participant_b), hits: match?.hits || (bout.format === "direct_elimination" ? detail.session.de_hits : detail.session.pool_hits) });
  };
  const saveScore = async (body) => {
    try { const { data } = await api.put(`/competitive-sessions/${id}/bouts/${score.bout.id}/result`, body); setDetail(data); setScore(null); toast.success("Risultato salvato"); }
    catch (e) { toast.error(e.response?.data?.detail || "Errore risultato"); }
  };
  const buildDe = async (body) => {
    try { const { data } = await api.post(`/competitive-sessions/${id}/de`, body); setDetail(data); toast.success("Tabellone generato"); }
    catch (e) { toast.error(e.response?.data?.detail || "Errore tabellone"); }
  };
  const resetDe = async () => {
    try { const { data } = await api.delete(`/competitive-sessions/${id}/de`); setDetail(data); }
    catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };
  const openPdf = async (e) => {
    e.preventDefault();
    try {
      const r = await api.get(`/competitive-sessions/${id}/pdf`, { responseType: "blob" });
      window.open(URL.createObjectURL(new Blob([r.data], { type: "application/pdf" })), "_blank");
    } catch { toast.error("Errore generazione PDF"); }
  };
  const importFromTraining = async () => {
    try {
      const { data } = await api.get(`/competitive-sessions/${id}/suggested-participants`);
      if (!data.athlete_ids.length) { toast.info("Nessun atleta nuovo da importare dalla seduta"); return; }
      await saveParticipants([...detail.session.participants, ...data.athlete_ids.map((aid) => ({ key: aid, athlete_id: aid }))]);
      toast.success(`${data.athlete_ids.length} atleti importati da ${data.source === "presenze" ? "presenze" : "roster"} della seduta`);
    } catch (e) { toast.error(e.response?.data?.detail || "Errore importazione"); }
  };

  if (!detail) return <div className="p-8 text-sm text-zinc-500">Caricamento…</div>;
  const { session, pools } = detail;
  const poolsDone = pools.length > 0 && pools.every((p) => p.completed);

  return (
    <div className="space-y-5" data-testid="competitive-session-page">
      <div>
        <Link to="/scherma?tab=simulation" className="inline-flex items-center gap-1 text-xs uppercase tracking-wider text-zinc-500 hover:text-black" data-testid="csess-back"><ArrowLeft className="w-3.5 h-3.5" />Simulazioni</Link>
        <div className="flex items-end justify-between flex-wrap gap-3 mt-2">
          <div>
            <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight" data-testid="csess-title">{session.name}</h1>
            <div className="text-sm text-zinc-500 font-mono">{session.date}{session.weapon ? ` · ${session.weapon}` : ""} · {session.participants.length} partecipanti · girone a {session.pool_hits} · ED a {session.de_hits}</div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {detail.training && (
              <Link to={`/allenamento/${detail.training.id}`} data-testid="csess-training-link" className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border border-zinc-300 bg-white hover:border-black">
                <CalendarDays className="w-3.5 h-3.5" />Seduta: {detail.training.title}{detail.training.group_names?.length ? ` · ${detail.training.group_names.join(", ")}` : ""}
              </Link>
            )}
            <a href={`${process.env.REACT_APP_BACKEND_URL}/api/competitive-sessions/${id}/pdf`} target="_blank" rel="noreferrer" data-testid="csess-pdf" onClick={openPdf} className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border border-zinc-300 bg-white hover:border-black"><Printer className="w-3.5 h-3.5" />Stampa PDF</a>
            <span className="text-xs px-2.5 py-1 rounded-full bg-zinc-900 text-white" data-testid="csess-status">{STATUS[session.status] || session.status}</span>
          </div>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="participants" data-testid="ctab-participants">Partecipanti</TabsTrigger>
          <TabsTrigger value="pools" data-testid="ctab-pools">Gironi{pools.length ? ` · ${pools.length}` : ""}</TabsTrigger>
          <TabsTrigger value="de" data-testid="ctab-de">Eliminazione diretta</TabsTrigger>
          <TabsTrigger value="ranking" data-testid="ctab-ranking">Classifica</TabsTrigger>
        </TabsList>
        <TabsContent value="participants" className="pt-5">
          <ParticipantsTab session={session} athletes={athletes} groups={groups} nameOf={nameOf} rankOf={rankOf} onSave={saveParticipants} onDistribute={distribute} locked={pools.length > 0} onImportFromTraining={detail.training ? importFromTraining : null} trainingTitle={detail.training?.title} />
        </TabsContent>
        <TabsContent value="pools" className="pt-5 space-y-4">
          {pools.length === 0 ? <div className="text-sm text-zinc-500" data-testid="pools-empty">Nessun girone: aggiungi i partecipanti e genera i gironi.</div> : (
            <>
              {!poolsDone && <div className="text-xs text-zinc-500">Clicca una cella o un assalto per inserire il risultato. Le classifiche si aggiornano in tempo reale.</div>}
              {poolsDone && !detail.de.length && <div className="text-xs text-emerald-700 font-medium" data-testid="pools-complete">Gironi conclusi: passa alla tab Eliminazione diretta per generare il tabellone.</div>}
              <div className="flex justify-end">
                {!editPools && <Button variant="outline" size="sm" data-testid="pools-edit" onClick={() => setEditPools(true)}>Modifica gironi</Button>}
              </div>
              {editPools && <PoolsEditor pools={pools} participants={session.participants} nameOf={nameOf} rankOf={rankOf} onSave={(d) => savePools(d, "Gironi aggiornati")} onCancel={() => setEditPools(false)} />}
              {pools.map((p) => <PoolCard key={p.id} pool={p} hits={session.pool_hits} nameOf={nameOf} onOpenBout={(b) => openBout(b)} />)}
            </>
          )}
        </TabsContent>
        <TabsContent value="de" className="pt-5">
          <BracketTab key={detail.de.length + ":" + detail.overall_ranking.length} detail={detail} nameOf={nameOf} onBuild={buildDe} onReset={resetDe} onOpenBout={openBout} />
        </TabsContent>
        <TabsContent value="ranking" className="pt-5"><RankingTab detail={detail} nameOf={nameOf} /></TabsContent>
      </Tabs>

      {score && <ScoreDialog open onOpenChange={(o) => !o && setScore(null)} nameA={score.nameA} nameB={score.nameB} hits={score.hits} bout={score.bout} onSave={saveScore} onReset={() => saveScore({ status: "scheduled" })} />}
    </div>
  );
}
